import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Ban, Check, ChevronLeft, ChevronRight, Pencil, Repeat } from 'lucide-react';

import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import { financialEntryApi, financialRecurrenceApi } from '../api';
import EditRecurrenceModal from '../EditRecurrenceModal';
import {
  FINANCIAL_ENTRY_STATUS,
  FINANCIAL_ENTRY_TYPE,
  FINANCIAL_VIEW_BY,
  getCategoryLabel,
} from '../constants';
import { formatCurrencyBRL } from '../financialFormatters';
import { FinancialEntryListFilters, FinancialEntryListRow } from '../types';
import { financialEntryRoutes } from '../routes';

import {
  CalendarScope,
  FINANCIAL_CALENDAR_SCOPE,
  MONTH_NAMES,
  WEEKDAY_LETTER,
  WEEKDAY_SHORT,
  dateKeyFromApi,
  gridBounds,
  isSameDay,
  monthWeeks,
  periodBounds,
  periodLabel,
  shiftAnchor,
  toDateKey,
  weekDays,
} from './calendarUtils';
import {
  CalendarRoot,
  ChipTitle,
  ChipValue,
  ChipVariant,
  DayNumber,
  EXPENSE_COLORS,
  EmptyDayHint,
  ErrorBox,
  EventChip,
  GridScroll,
  LoadingHint,
  MiniDay,
  MiniDayNumber,
  MiniDot,
  MiniDots,
  MiniGrid,
  MiniMonth,
  MiniMonthTitle,
  MiniWeekdays,
  MonthBody,
  MonthDayCell,
  MonthWeekRow,
  MoreLink,
  OccurrenceMenu,
  OccurrenceMenuHeader,
  OccurrenceMenuItem,
  OccurrenceMenuMeta,
  OccurrenceMenuTitle,
  NavButton,
  NavGroup,
  PeriodTitle,
  REVENUE_COLORS,
  Toolbar,
  TotalCard,
  TotalLabel,
  TotalValue,
  TotalsGroup,
  WeekBody,
  WeekColumn,
  WeekdayCell,
  WeekdayHeader,
  WeekdayNumber,
  YearGrid,
} from './style';

/** Quantidade máxima de linhas buscadas de uma vez para montar o calendário. */
const CALENDAR_FETCH_LIMIT = 2000;
/** Chips visíveis por dia na visão de mês antes do "+N mais". */
const MONTH_MAX_CHIPS = 3;

interface CalendarEvent {
  key: string;
  dateKey: string;
  entryId: number | null;
  virtual: boolean;
  canceled: boolean;
  type: number | null;
  value: number | null;
  title: string;
  tooltip: string;
  recurrenceId: number | null;
  occurrenceDate: string | null;
}

interface PeriodTotals {
  revenue: number;
  expense: number;
}

interface FinancialCalendarProps {
  scope: CalendarScope;
  onScopeChange: (scope: CalendarScope) => void;
  search: string;
  filters: FinancialEntryListFilters;
  refreshKey: number;
}

const eventVariant = (type: number | null): ChipVariant => {
  if (type === FINANCIAL_ENTRY_TYPE.REVENUE) return 'revenue';
  if (type === FINANCIAL_ENTRY_TYPE.EXPENSE) return 'expense';
  return 'neutral';
};

const entryTitle = (row: FinancialEntryListRow): string =>
  row.description?.trim() || getCategoryLabel(row.category);

/**
 * Converte as linhas da listagem em eventos de calendário: uma ocorrência por
 * parcela (data de vencimento) e uma por recorrência prevista.
 */
const buildEvents = (rows: FinancialEntryListRow[]): CalendarEvent[] => {
  const events: CalendarEvent[] = [];

  rows.forEach((row) => {
    const title = entryTitle(row);
    const canceled = row.status === FINANCIAL_ENTRY_STATUS.CANCELED;
    // A contraparte do lançamento: quem paga numa entrada, quem recebe numa saída.
    const counterparty =
      row.type === FINANCIAL_ENTRY_TYPE.EXPENSE ? row.receiver : row.payer;
    const company = counterparty ? ` · ${counterparty}` : '';

    if (row.virtual) {
      const dateKey = dateKeyFromApi(row.dueDate ?? row.occurrenceDate);
      if (!dateKey) return;
      events.push({
        key: `occ-${row.occurrenceKey}`,
        dateKey,
        entryId: null,
        virtual: true,
        canceled: false,
        type: row.type,
        value: row.totalValue,
        title,
        tooltip: `${title}${company} · Previsto (recorrência)`,
        recurrenceId: row.recurrenceId ?? null,
        occurrenceDate: dateKeyFromApi(row.occurrenceDate ?? row.dueDate),
      });
      return;
    }

    const installments = row.installments ?? [];
    const count = installments.length;

    installments.forEach((installment, index) => {
      const dateKey = dateKeyFromApi(installment.dueDate);
      if (!dateKey) return;
      const suffix = count > 1 ? ` (${installment.installmentNumber?.trim() || `${index + 1}/${count}`})` : '';
      events.push({
        key: `entry-${row.id}-inst-${installment.id}`,
        dateKey,
        entryId: row.id,
        virtual: false,
        canceled,
        type: row.type,
        value: installment.value ?? (count === 1 ? row.totalValue : null),
        title: `${title}${suffix}`,
        tooltip: `${title}${suffix}${company}${canceled ? ' · Cancelado' : ''}`,
        recurrenceId: null,
        occurrenceDate: null,
      });
    });
  });

  return events.sort((a, b) => a.dateKey.localeCompare(b.dateKey));
};

const sumTotals = (events: CalendarEvent[], from: Date, to: Date): PeriodTotals => {
  const fromKey = toDateKey(from);
  const toKey = toDateKey(to);
  return events.reduce<PeriodTotals>(
    (acc, event) => {
      if (event.canceled) return acc;
      if (event.dateKey < fromKey || event.dateKey > toKey) return acc;
      const value = event.value ?? 0;
      if (event.type === FINANCIAL_ENTRY_TYPE.REVENUE) acc.revenue += value;
      else if (event.type === FINANCIAL_ENTRY_TYPE.EXPENSE) acc.expense += value;
      return acc;
    },
    { revenue: 0, expense: 0 },
  );
};

const groupByDay = (events: CalendarEvent[]): Map<string, CalendarEvent[]> => {
  const map = new Map<string, CalendarEvent[]>();
  events.forEach((event) => {
    const list = map.get(event.dateKey);
    if (list) list.push(event);
    else map.set(event.dateKey, [event]);
  });
  return map;
};

const FinancialCalendar: React.FC<FinancialCalendarProps> = ({
  scope,
  onScopeChange,
  search,
  filters,
  refreshKey,
}) => {
  const navigate = useNavigate();
  const { setFeedback } = useContext(FeedbackContext);
  const today = useMemo(() => new Date(), []);
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [pendingEvent, setPendingEvent] = useState<CalendarEvent | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<DOMRect | null>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const activeChipRef = useRef<HTMLButtonElement | null>(null);
  const menuOpenedAtRef = useRef(0);
  const [materializing, setMaterializing] = useState(false);
  const [recurrenceEdit, setRecurrenceEdit] = useState<{
    recurrenceId: number;
    effectiveFrom: string;
  } | null>(null);

  const period = useMemo(() => periodBounds(scope, anchor), [scope, anchor]);
  const grid = useMemo(() => gridBounds(scope, anchor), [scope, anchor]);

  const queryFilters = useMemo<FinancialEntryListFilters>(
    () => ({
      viewBy: FINANCIAL_VIEW_BY.ENTRY,
      sortBy: 'dueDate',
      appliedFilters: filters.appliedFilters ?? [],
      search: search || undefined,
      occurrenceFrom: toDateKey(grid.from),
      occurrenceTo: toDateKey(grid.to),
      page: 1,
      limit: CALENDAR_FETCH_LIMIT,
    }),
    [filters.appliedFilters, search, grid],
  );

  const { data, error, isError, isFetching, refetch } = useQuery({
    queryKey: ['financialCalendar', queryFilters, refreshKey],
    queryFn: () => financialEntryApi.list(queryFilters),
  });

  const events = useMemo(() => buildEvents(data?.data ?? []), [data]);
  const eventsByDay = useMemo(() => groupByDay(events), [events]);
  const totals = useMemo(
    () => sumTotals(events, period.from, period.to),
    [events, period],
  );

  /** Efetiva ou reprova a ocorrência prevista selecionada no menu do calendário. */
  const resolvePendingOccurrence = async (cancel: boolean) => {
    if (!pendingEvent || materializing) return;
    try {
      setMaterializing(true);
      await financialRecurrenceApi.materialize(pendingEvent.recurrenceId!, {
        occurrenceDate: pendingEvent.occurrenceDate!,
        ...(cancel ? { cancel: true } : {}),
      });
      setFeedback({
        message: cancel
          ? 'Lançamento previsto reprovado com sucesso.'
          : 'Lançamento efetivado com sucesso.',
        isOpen: true,
        statusOk: true,
      });
      setPendingEvent(null);
      refetch();
    } catch (err) {
      setFeedback({
        message:
          (err as any)?.response?.data?.message ||
          (cancel
            ? 'Erro ao reprovar o lançamento previsto.'
            : 'Erro ao efetivar o lançamento.'),
        isOpen: true,
        statusOk: false,
      });
    } finally {
      setMaterializing(false);
    }
  };

  const editOnlyThisOccurrence = async () => {
    if (!pendingEvent?.recurrenceId || !pendingEvent.occurrenceDate || materializing) return;
    try {
      setMaterializing(true);
      const detail = await financialRecurrenceApi.materialize(pendingEvent.recurrenceId, {
        occurrenceDate: pendingEvent.occurrenceDate,
      });
      setPendingEvent(null);
      navigate(financialEntryRoutes.edit(detail.id));
    } catch (err) {
      setFeedback({
        message:
          (err as any)?.response?.data?.message ||
          'Erro ao preparar a edição desta ocorrência.',
        isOpen: true,
        statusOk: false,
      });
    } finally {
      setMaterializing(false);
    }
  };

  const editFromThisOccurrence = () => {
    if (!pendingEvent?.recurrenceId || !pendingEvent.occurrenceDate || materializing) return;
    setRecurrenceEdit({
      recurrenceId: pendingEvent.recurrenceId,
      effectiveFrom: pendingEvent.occurrenceDate,
    });
    setPendingEvent(null);
    setMenuAnchor(null);
  };

  const closeOccurrenceMenu = () => {
    if (materializing) return;
    setPendingEvent(null);
    setMenuAnchor(null);
  };

  useLayoutEffect(() => {
    if (!pendingEvent || !menuAnchor || !menuRef.current) return;
    const menu = menuRef.current.getBoundingClientRect();
    const gap = 4;
    const margin = 8;
    const spaceBelow = window.innerHeight - menuAnchor.bottom;
    const openUp = spaceBelow < menu.height + gap && menuAnchor.top > menu.height + gap;
    let left = menuAnchor.left;
    if (left + menu.width > window.innerWidth - margin) {
      left = menuAnchor.right - menu.width;
    }
    left = Math.max(margin, Math.min(left, window.innerWidth - menu.width - margin));
    const top = openUp
      ? Math.max(margin, menuAnchor.top - menu.height - gap)
      : Math.min(menuAnchor.bottom + gap, window.innerHeight - menu.height - margin);
    setMenuPos((prev) => (prev.top === top && prev.left === left ? prev : { top, left }));
  }, [pendingEvent, menuAnchor]);

  useEffect(() => {
    if (!pendingEvent) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (activeChipRef.current?.contains(target)) return;
      closeOccurrenceMenu();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeOccurrenceMenu();
    };
    const onDismiss = () => {
      if (Date.now() - menuOpenedAtRef.current < 200) return;
      closeOccurrenceMenu();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onDismiss);
    window.addEventListener('scroll', onDismiss, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onDismiss);
      window.removeEventListener('scroll', onDismiss, true);
    };
  }, [pendingEvent, materializing]);

  const openDay = (date: Date) => {
    setAnchor(date);
    onScopeChange(FINANCIAL_CALENDAR_SCOPE.WEEK);
  };

  const openMonth = (date: Date) => {
    setAnchor(date);
    onScopeChange(FINANCIAL_CALENDAR_SCOPE.MONTH);
  };

  const openOccurrenceMenu = (calendarEvent: CalendarEvent, anchor: HTMLButtonElement) => {
    if (materializing) return;
    if (pendingEvent?.key === calendarEvent.key) {
      setPendingEvent(null);
      setMenuAnchor(null);
      return;
    }
    activeChipRef.current = anchor;
    menuOpenedAtRef.current = Date.now();
    const rect = anchor.getBoundingClientRect();
    setMenuAnchor(rect);
    setMenuPos({
      top: rect.bottom + 4,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 248)),
    });
    setPendingEvent(calendarEvent);
  };

  const renderChip = (event: CalendarEvent) => {
    // Reais abrem a edição; previstos abrem o menu de ações no próprio item.
    const clickable = event.virtual
      ? event.recurrenceId !== null && event.occurrenceDate !== null
      : event.entryId !== null;
    const handleClick = (clickEvent: React.MouseEvent<HTMLButtonElement>) => {
      if (event.virtual) openOccurrenceMenu(event, clickEvent.currentTarget);
      else navigate(financialEntryRoutes.edit(event.entryId!));
    };
    return (
      <EventChip
        key={event.key}
        type="button"
        title={event.tooltip}
        $variant={eventVariant(event.type)}
        $virtual={event.virtual}
        $canceled={event.canceled}
        $clickable={clickable}
        $menuOpen={pendingEvent?.key === event.key}
        onClick={clickable ? handleClick : undefined}
      >
        <ChipTitle>{event.title}</ChipTitle>
        <ChipValue>{formatCurrencyBRL(event.value) || '—'}</ChipValue>
      </EventChip>
    );
  };

  const renderWeek = () => {
    const days = weekDays(anchor);
    return (
      <>
        <WeekdayHeader>
          {days.map((day, index) => {
            const isToday = isSameDay(day, today);
            return (
              <WeekdayCell key={toDateKey(day)} $today={isToday}>
                {WEEKDAY_SHORT[index]}
                <WeekdayNumber $today={isToday}>{day.getDate()}</WeekdayNumber>
              </WeekdayCell>
            );
          })}
        </WeekdayHeader>
        <WeekBody>
          {days.map((day) => {
            const dayEvents = eventsByDay.get(toDateKey(day)) ?? [];
            return (
              <WeekColumn key={toDateKey(day)} $today={isSameDay(day, today)}>
                {dayEvents.length ? (
                  dayEvents.map(renderChip)
                ) : (
                  <EmptyDayHint>Sem lançamentos</EmptyDayHint>
                )}
              </WeekColumn>
            );
          })}
        </WeekBody>
      </>
    );
  };

  const renderMonth = () => {
    const weeks = monthWeeks(anchor);
    const currentMonth = anchor.getMonth();
    return (
      <>
        <WeekdayHeader>
          {WEEKDAY_SHORT.map((label) => (
            <WeekdayCell key={label}>{label}</WeekdayCell>
          ))}
        </WeekdayHeader>
        <MonthBody>
          {weeks.map((week) => (
            <MonthWeekRow key={toDateKey(week[0])}>
              {week.map((day) => {
                const key = toDateKey(day);
                const dayEvents = eventsByDay.get(key) ?? [];
                const outside = day.getMonth() !== currentMonth;
                const hidden = dayEvents.length - MONTH_MAX_CHIPS;
                return (
                  <MonthDayCell key={key} $outside={outside}>
                    <DayNumber
                      type="button"
                      title="Abrir semana"
                      $outside={outside}
                      $today={isSameDay(day, today)}
                      onClick={() => openDay(day)}
                    >
                      {day.getDate()}
                    </DayNumber>
                    {dayEvents.slice(0, MONTH_MAX_CHIPS).map(renderChip)}
                    {hidden > 0 && (
                      <MoreLink type="button" onClick={() => openDay(day)}>
                        +{hidden} mais
                      </MoreLink>
                    )}
                  </MonthDayCell>
                );
              })}
            </MonthWeekRow>
          ))}
        </MonthBody>
      </>
    );
  };

  const renderYear = () => {
    const year = anchor.getFullYear();
    return (
      <YearGrid>
        {MONTH_NAMES.map((name, monthIndex) => {
          const monthDate = new Date(year, monthIndex, 1);
          const weeks = monthWeeks(monthDate);
          const isCurrentMonth =
            today.getFullYear() === year && today.getMonth() === monthIndex;
          return (
            <MiniMonth key={name}>
              <MiniMonthTitle
                type="button"
                title="Abrir mês"
                $current={isCurrentMonth}
                onClick={() => openMonth(monthDate)}
              >
                {name}
              </MiniMonthTitle>
              <MiniWeekdays>
                {WEEKDAY_LETTER.map((letter, index) => (
                  <span key={`${name}-${index}`}>{letter}</span>
                ))}
              </MiniWeekdays>
              <MiniGrid>
                {weeks.flat().map((day) => {
                  const key = toDateKey(day);
                  const outside = day.getMonth() !== monthIndex;
                  const dayEvents = outside ? [] : eventsByDay.get(key) ?? [];
                  const hasRevenue = dayEvents.some(
                    (event) => !event.canceled && event.type === FINANCIAL_ENTRY_TYPE.REVENUE,
                  );
                  const hasExpense = dayEvents.some(
                    (event) => !event.canceled && event.type === FINANCIAL_ENTRY_TYPE.EXPENSE,
                  );
                  return (
                    <MiniDay
                      key={key}
                      type="button"
                      $outside={outside}
                      $hasEvents={dayEvents.length > 0}
                      title={dayEvents.length ? `${dayEvents.length} lançamento(s)` : undefined}
                      onClick={dayEvents.length ? () => openDay(day) : undefined}
                    >
                      <MiniDayNumber $today={!outside && isSameDay(day, today)}>
                        {day.getDate()}
                      </MiniDayNumber>
                      <MiniDots>
                        {hasRevenue && <MiniDot $color={REVENUE_COLORS.dot} />}
                        {hasExpense && <MiniDot $color={EXPENSE_COLORS.dot} />}
                      </MiniDots>
                    </MiniDay>
                  );
                })}
              </MiniGrid>
            </MiniMonth>
          );
        })}
      </YearGrid>
    );
  };

  return (
    <CalendarRoot>
      <Toolbar>
        <NavGroup>
          <NavButton
            type="button"
            title="Período anterior"
            onClick={() => setAnchor((prev) => shiftAnchor(scope, prev, -1))}
          >
            <ChevronLeft size={16} />
          </NavButton>
          <NavButton
            type="button"
            title="Próximo período"
            onClick={() => setAnchor((prev) => shiftAnchor(scope, prev, 1))}
          >
            <ChevronRight size={16} />
          </NavButton>
          <NavButton type="button" onClick={() => setAnchor(new Date())}>
            Hoje
          </NavButton>
          <PeriodTitle>{periodLabel(scope, anchor)}</PeriodTitle>
          {isFetching && <LoadingHint>Carregando...</LoadingHint>}
        </NavGroup>

        <TotalsGroup>
          <TotalCard $variant="revenue" title="Soma das entradas no período">
            <TotalLabel>Entradas</TotalLabel>
            <TotalValue>{formatCurrencyBRL(totals.revenue)}</TotalValue>
          </TotalCard>
          <TotalCard $variant="expense" title="Soma das saídas no período">
            <TotalLabel>Saídas</TotalLabel>
            <TotalValue>{formatCurrencyBRL(totals.expense)}</TotalValue>
          </TotalCard>
        </TotalsGroup>
      </Toolbar>

      {isError ? (
        <ErrorBox>
          Erro ao carregar lançamentos: {(error as Error)?.message ?? 'Tente novamente.'}
        </ErrorBox>
      ) : (
        <GridScroll>
          {scope === FINANCIAL_CALENDAR_SCOPE.WEEK && renderWeek()}
          {scope === FINANCIAL_CALENDAR_SCOPE.MONTH && renderMonth()}
          {scope === FINANCIAL_CALENDAR_SCOPE.YEAR && renderYear()}
        </GridScroll>
      )}

      {pendingEvent &&
        ReactDOM.createPortal(
          <OccurrenceMenu
            ref={menuRef}
            role="menu"
            $top={menuPos.top}
            $left={menuPos.left}
          >
            <OccurrenceMenuHeader>
              <OccurrenceMenuTitle>{pendingEvent.title}</OccurrenceMenuTitle>
              <OccurrenceMenuMeta>
                {formatCurrencyBRL(pendingEvent.value)
                  ? `${formatCurrencyBRL(pendingEvent.value)} · `
                  : ''}
                Previsto para {pendingEvent.occurrenceDate!.split('-').reverse().join('/')}
              </OccurrenceMenuMeta>
            </OccurrenceMenuHeader>
            <OccurrenceMenuItem
              type="button"
              role="menuitem"
              disabled={materializing}
              onClick={() => editOnlyThisOccurrence()}
            >
              <Pencil size={15} />
              Apenas este
            </OccurrenceMenuItem>
            <OccurrenceMenuItem
              type="button"
              role="menuitem"
              disabled={materializing}
              onClick={editFromThisOccurrence}
            >
              <Repeat size={15} />
              Este e os próximos
            </OccurrenceMenuItem>
            <OccurrenceMenuItem
              type="button"
              role="menuitem"
              disabled={materializing}
              onClick={() => resolvePendingOccurrence(false)}
            >
              <Check size={15} />
              Efetivar lançamento
            </OccurrenceMenuItem>
            <OccurrenceMenuItem
              type="button"
              role="menuitem"
              $danger
              disabled={materializing}
              onClick={() => resolvePendingOccurrence(true)}
            >
              <Ban size={15} />
              Reprovar
            </OccurrenceMenuItem>
          </OccurrenceMenu>,
          document.body,
        )}

      {recurrenceEdit && (
        <EditRecurrenceModal
          recurrenceId={recurrenceEdit.recurrenceId}
          effectiveFrom={recurrenceEdit.effectiveFrom}
          onClose={() => setRecurrenceEdit(null)}
          onUpdated={() => {
            setRecurrenceEdit(null);
            refetch();
          }}
        />
      )}
    </CalendarRoot>
  );
};

export default FinancialCalendar;
